/**
 * Audit Dashboard Component
 *
 * Main interface for running facility audits and viewing results.
 * Features:
 * - Run audit button with loading state
 * - AI-powered summary card
 * - Severity filter tabs
 * - Expandable category cards with exception details
 * - Sort by last name within categories
 * - Export to Excel
 * - Direct links to Storable Edge
 */

import { useState, useEffect } from 'react'
import {
  Play, RefreshCw, Loader2, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, ExternalLink, Clock, DollarSign,
  FileText, Shield, CreditCard, Lock, Key, User, Filter,
  Download, Calendar, Banknote
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { api } from '../lib/api'
import { generateEdgeLink } from '../lib/edge-links'
import AuditChat from './AuditChat'

// Category icons mapping
const categoryIcons = {
  lease_documentation: FileText,
  insurance_coverage: Shield,
  billing_rates: CreditCard,
  delinquency: Lock,
  access_control: Key,
  tenant_records: User
}

// Category display names
const categoryNames = {
  lease_documentation: 'Lease & Documentation',
  insurance_coverage: 'Insurance Coverage',
  billing_rates: 'Billing & Rates',
  delinquency: 'Delinquency',
  access_control: 'Access Control',
  tenant_records: 'Tenant Records'
}

// Severity colors
const severityStyles = {
  critical: 'badge-critical',
  high: 'badge-high',
  medium: 'badge-medium',
  low: 'badge-low'
}

// Severity order for sorting
const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 }

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Format currency helper
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-'
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Sort exceptions by last name
const sortByLastName = (exceptions) => {
  return [...exceptions].sort((a, b) => {
    const lastNameA = (a.tenant?.lastName || a.tenant?.name?.split(' ').pop() || 'ZZZ').toLowerCase()
    const lastNameB = (b.tenant?.lastName || b.tenant?.name?.split(' ').pop() || 'ZZZ').toLowerCase()
    return lastNameA.localeCompare(lastNameB)
  })
}

export default function AuditDashboard() {
  const [facilities, setFacilities] = useState([])
  const [selectedFacility, setSelectedFacility] = useState(null)
  const [auditResults, setAuditResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingFacilities, setLoadingFacilities] = useState(true)
  const [error, setError] = useState(null)
  const [severityFilter, setSeverityFilter] = useState('all')
  const [expandedCategories, setExpandedCategories] = useState({})
  const [expandedExceptions, setExpandedExceptions] = useState({})

  // Fetch facilities on mount
  useEffect(() => {
    fetchFacilities()
  }, [])

  // Fetch latest audit when facility changes
  useEffect(() => {
    if (selectedFacility) {
      fetchLatestAudit()
    }
  }, [selectedFacility])

  const fetchFacilities = async () => {
    try {
      const { facilities } = await api.get('/facilities')
      setFacilities(facilities.filter(f => f.is_active))
      if (facilities.length > 0) {
        setSelectedFacility(facilities[0])
      }
    } catch (err) {
      setError('Failed to load facilities')
    } finally {
      setLoadingFacilities(false)
    }
  }

  const fetchLatestAudit = async () => {
    if (!selectedFacility) return
    try {
      const data = await api.get(`/audit/${selectedFacility.id}/latest`)
      setAuditResults(data)
      setError(null)
    } catch (err) {
      if (err.status !== 404) {
        console.error('Failed to fetch latest audit:', err)
      }
      setAuditResults(null)
    }
  }

  const runAudit = async () => {
    if (!selectedFacility) return
    setLoading(true)
    setError(null)
    try {
      const data = await api.post(`/audit/${selectedFacility.id}/run`)
      setAuditResults(data)
      // Reset expansions
      setExpandedCategories({})
      setExpandedExceptions({})
    } catch (err) {
      setError(err.message || 'Audit failed')
    } finally {
      setLoading(false)
    }
  }

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  const toggleException = (exceptionId) => {
    setExpandedExceptions(prev => ({
      ...prev,
      [exceptionId]: !prev[exceptionId]
    }))
  }

  // Export to Excel - can filter by category and/or severity
  const exportToExcel = (categoryFilter = null, severityFilterOverride = null) => {
    if (!auditResults?.results?.exceptions) return

    let exceptions = auditResults.results.exceptions

    // Apply category filter if specified
    if (categoryFilter) {
      exceptions = exceptions.filter(e => e.category === categoryFilter)
    }

    // Apply severity filter (use override if provided, otherwise use current filter)
    const effectiveSeverityFilter = severityFilterOverride !== null ? severityFilterOverride : severityFilter
    if (effectiveSeverityFilter && effectiveSeverityFilter !== 'all') {
      exceptions = exceptions.filter(e => e.severity === effectiveSeverityFilter)
    }

    if (exceptions.length === 0) {
      alert('No exceptions to export with the current filters.')
      return
    }

    // Prepare data for export
    const exportData = exceptions.map(e => ({
      'Severity': e.severity?.toUpperCase() || '',
      'Category': categoryNames[e.category] || e.category,
      'Issue': e.title,
      'Description': e.description,
      'Tenant Name': e.tenant?.name || '',
      'Last Name': e.tenant?.lastName || '',
      'First Name': e.tenant?.firstName || '',
      'Unit': e.unit?.number || '',
      'Email': e.tenant?.email || '',
      'Phone': e.tenant?.phone || '',
      'Move-In Date': e.tenant?.moveInDate ? formatDate(e.tenant.moveInDate) : '',
      'Monthly Rent': e.tenant?.monthlyRent ? `$${e.tenant.monthlyRent}` : '',
      'Paid Through': e.tenant?.paidThroughDate ? formatDate(e.tenant.paidThroughDate) : '',
      'Balance': e.tenant?.balance ? `$${e.tenant.balance}` : '$0',
      'Delinquency Status': e.tenant?.delinquencyStatus || '',
      'Days Past Due': e.tenant?.pastDueDays || 0,
      'Lease Sent Date': e.tenant?.leaseSentDate ? formatDate(e.tenant.leaseSentDate) : '',
      'Edge Link': e.edgeLink || ''
    }))

    // Sort by severity then last name
    exportData.sort((a, b) => {
      const sevA = severityOrder[a.Severity.toLowerCase()] ?? 99
      const sevB = severityOrder[b.Severity.toLowerCase()] ?? 99
      if (sevA !== sevB) return sevA - sevB
      return (a['Last Name'] || '').localeCompare(b['Last Name'] || '')
    })

    // Create workbook
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(exportData)

    // Set column widths
    ws['!cols'] = [
      { wch: 10 }, // Severity
      { wch: 20 }, // Category
      { wch: 30 }, // Issue
      { wch: 40 }, // Description
      { wch: 20 }, // Tenant Name
      { wch: 15 }, // Last Name
      { wch: 15 }, // First Name
      { wch: 10 }, // Unit
      { wch: 25 }, // Email
      { wch: 15 }, // Phone
      { wch: 12 }, // Move-In Date
      { wch: 12 }, // Monthly Rent
      { wch: 12 }, // Paid Through
      { wch: 10 }, // Balance
      { wch: 15 }, // Delinquency Status
      { wch: 12 }, // Days Past Due
      { wch: 12 }, // Lease Sent Date
      { wch: 50 }, // Edge Link
    ]

    // Build sheet name
    let sheetName = 'Audit Exceptions'
    if (categoryFilter) {
      sheetName = (categoryNames[categoryFilter] || categoryFilter).substring(0, 31)
    }

    XLSX.utils.book_append_sheet(wb, ws, sheetName)

    // Generate filename with date and filters
    const date = new Date().toISOString().split('T')[0]
    const facilityName = selectedFacility?.name?.replace(/[^a-z0-9]/gi, '_') || 'facility'
    let filename = `audit_${facilityName}`
    if (categoryFilter) {
      filename += `_${categoryFilter}`
    }
    if (effectiveSeverityFilter && effectiveSeverityFilter !== 'all') {
      filename += `_${effectiveSeverityFilter}`
    }
    filename += `_${date}.xlsx`

    // Download
    XLSX.writeFile(wb, filename)
  }

  // Filter exceptions by severity
  const filteredExceptions = auditResults?.results?.exceptions?.filter(e =>
    severityFilter === 'all' || e.severity === severityFilter
  ) || []

  // Group exceptions by category, sorted by last name within each
  const exceptionsByCategory = filteredExceptions.reduce((acc, exception) => {
    const cat = exception.category
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(exception)
    return acc
  }, {})

  // Sort each category by last name
  Object.keys(exceptionsByCategory).forEach(cat => {
    exceptionsByCategory[cat] = sortByLastName(exceptionsByCategory[cat])
  })

  if (loadingFacilities) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    )
  }

  if (facilities.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card text-center py-12">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Facilities Configured</h2>
          <p className="text-gray-500 mb-4">
            Add a facility in settings to start running audits.
          </p>
          <a href="/settings" className="btn-primary inline-flex items-center gap-2">
            Go to Settings
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Facility Audit</h1>
          <p className="text-gray-500">Run automated checks on your facility</p>
        </div>

        <div className="flex items-center gap-4">
          {/* Facility Selector */}
          <select
            value={selectedFacility?.id || ''}
            onChange={(e) => {
              const facility = facilities.find(f => f.id === e.target.value)
              setSelectedFacility(facility)
              setAuditResults(null)
            }}
            className="input max-w-xs"
          >
            {facilities.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>

          {/* Export Button - exports based on current severity filter */}
          {auditResults?.results?.exceptions?.length > 0 && (
            <button
              onClick={() => exportToExcel(null, severityFilter)}
              className="btn-secondary flex items-center gap-2"
              title={severityFilter === 'all' ? 'Export all exceptions' : `Export ${severityFilter} severity only`}
            >
              <Download className="w-4 h-4" />
              Export {severityFilter !== 'all' ? `${severityFilter.charAt(0).toUpperCase() + severityFilter.slice(1)} ` : ''}to Excel
            </button>
          )}

          {/* Run Audit Button */}
          <button
            onClick={runAudit}
            disabled={loading || !selectedFacility}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Running Audit...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Audit
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">Audit Error</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Results Section */}
      {auditResults && (
        <>
          {/* AI Summary Card */}
          {auditResults.summary && (
            <div className="card bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-200">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span className="text-indigo-600">AI</span> Summary
                  </h2>
                  <p className="text-sm text-gray-500">
                    Generated {new Date(auditResults.created_at).toLocaleString()}
                  </p>
                </div>
                {auditResults.summary.revenueAtRisk > 0 && (
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Revenue at Risk</p>
                    <p className="text-2xl font-bold text-red-600">
                      ${auditResults.summary.revenueAtRisk.toLocaleString()}<span className="text-sm font-normal">/mo</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Headline */}
              <p className="text-gray-800 text-lg mb-6">{auditResults.summary.headline}</p>

              {/* Top Priorities */}
              {auditResults.summary.topPriorities?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-3">Top Priorities</h3>
                  <div className="space-y-3">
                    {auditResults.summary.topPriorities.map((priority, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/80 border border-indigo-100">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm flex items-center justify-center font-medium">
                          {i + 1}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{priority.title}</p>
                          <p className="text-sm text-gray-600">{priority.description}</p>
                          {priority.estimatedImpact && (
                            <p className="text-xs text-indigo-600 mt-1">
                              Impact: {priority.estimatedImpact}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Patterns and Quick Wins */}
              <div className="grid md:grid-cols-2 gap-6">
                {auditResults.summary.patterns?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Patterns Detected</h3>
                    <ul className="space-y-2">
                      {auditResults.summary.patterns.map((pattern, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          {pattern}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {auditResults.summary.quickWins?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Quick Wins</h3>
                    <ul className="space-y-2">
                      {auditResults.summary.quickWins.map((win, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {win}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="card text-center">
              <p className="text-3xl font-bold text-gray-900">
                {auditResults.results?.totalExceptions || 0}
              </p>
              <p className="text-sm text-gray-500">Total Issues</p>
            </div>
            <div className="card text-center">
              <p className="text-3xl font-bold text-red-600">
                {auditResults.results?.bySeverity?.critical || 0}
              </p>
              <p className="text-sm text-gray-500">Critical</p>
            </div>
            <div className="card text-center">
              <p className="text-3xl font-bold text-orange-600">
                {auditResults.results?.bySeverity?.high || 0}
              </p>
              <p className="text-sm text-gray-500">High</p>
            </div>
            <div className="card text-center">
              <p className="text-3xl font-bold text-amber-600">
                {auditResults.results?.bySeverity?.medium || 0}
              </p>
              <p className="text-sm text-gray-500">Medium</p>
            </div>
            <div className="card text-center">
              <p className="text-3xl font-bold text-blue-600">
                {auditResults.results?.bySeverity?.low || 0}
              </p>
              <p className="text-sm text-gray-500">Low</p>
            </div>
          </div>

          {/* Severity Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <div className="flex gap-1">
              {['all', 'critical', 'high', 'medium', 'low'].map(sev => (
                <button
                  key={sev}
                  onClick={() => setSeverityFilter(sev)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                             ${severityFilter === sev
                               ? 'bg-indigo-100 text-indigo-700'
                               : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  {sev.charAt(0).toUpperCase() + sev.slice(1)}
                </button>
              ))}
            </div>
            <span className="text-sm text-gray-400 ml-4">
              Sorted by last name within each category
            </span>
          </div>

          {/* Exception Categories */}
          <div className="space-y-4">
            {Object.entries(exceptionsByCategory).map(([category, exceptions]) => {
              const Icon = categoryIcons[category] || AlertTriangle
              const isExpanded = expandedCategories[category]

              return (
                <div key={category} className="card p-0 overflow-hidden">
                  {/* Category Header */}
                  <div className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="flex items-center gap-3 flex-1"
                    >
                      <div className="p-2 rounded-lg bg-indigo-100">
                        <Icon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900">
                          {categoryNames[category] || category}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {exceptions.length} issue{exceptions.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      {/* Export this category */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          exportToExcel(category, severityFilter)
                        }}
                        className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                        title={`Export ${categoryNames[category] || category} to Excel`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={() => toggleCategory(category)}>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Exception List */}
                  {isExpanded && (
                    <div className="border-t border-gray-200">
                      {exceptions.map((exception) => {
                        const isExExpanded = expandedExceptions[exception.id]

                        return (
                          <div
                            key={exception.id}
                            className="border-b border-gray-100 last:border-b-0"
                          >
                            {/* Exception Header */}
                            <button
                              onClick={() => toggleException(exception.id)}
                              className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors text-left"
                            >
                              <span className={severityStyles[exception.severity]}>
                                {exception.severity}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">
                                  {exception.title}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                  {exception.tenant?.name || 'N/A'}
                                  {exception.unit && ` • Unit ${exception.unit.number}`}
                                </p>
                              </div>
                              {exception.edgeLink && (
                                <a
                                  href={exception.edgeLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
                                >
                                  Open in Edge
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                              {isExExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )}
                            </button>

                            {/* Exception Details */}
                            {isExExpanded && (
                              <div className="px-4 pb-4 pt-0 ml-[88px] space-y-3">
                                <p className="text-gray-700">{exception.description}</p>

                                {/* Tenant Details Grid */}
                                {exception.tenant && (
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                                    <div>
                                      <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        Move-In Date
                                      </p>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatDate(exception.tenant.moveInDate)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <Banknote className="w-3 h-3" />
                                        Monthly Rent
                                      </p>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatCurrency(exception.tenant.monthlyRent)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        Paid Through
                                      </p>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatDate(exception.tenant.paidThroughDate)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <DollarSign className="w-3 h-3" />
                                        Delinquency
                                      </p>
                                      <p className={`text-sm font-medium ${
                                        exception.tenant.delinquencyStatus === 'current' ? 'text-green-600' :
                                        exception.tenant.pastDueDays > 30 ? 'text-red-600' :
                                        exception.tenant.pastDueDays > 0 ? 'text-amber-600' : 'text-gray-900'
                                      }`}>
                                        {exception.tenant.delinquencyStatus === 'current' ? 'Current' :
                                         exception.tenant.pastDueDays > 0 ? `${exception.tenant.pastDueDays} days past due` :
                                         exception.tenant.delinquencyStatus || 'Current'}
                                      </p>
                                    </div>
                                    {exception.tenant.leaseSentDate && (
                                      <div className="col-span-2">
                                        <p className="text-xs text-gray-500 flex items-center gap-1">
                                          <FileText className="w-3 h-3" />
                                          Lease Sent
                                        </p>
                                        <p className="text-sm font-medium text-gray-900">
                                          {formatDate(exception.tenant.leaseSentDate)}
                                        </p>
                                      </div>
                                    )}
                                    {exception.tenant.balance > 0 && (
                                      <div className="col-span-2">
                                        <p className="text-xs text-gray-500">Balance Due</p>
                                        <p className="text-sm font-medium text-red-600">
                                          {formatCurrency(exception.tenant.balance)}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Additional Details Grid */}
                                {exception.details && Object.keys(exception.details).length > 0 && (
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {Object.entries(exception.details).map(([key, value]) => (
                                      <div key={key} className="p-2 rounded bg-gray-50 border border-gray-100">
                                        <p className="text-xs text-gray-500 capitalize">
                                          {key.replace(/_/g, ' ')}
                                        </p>
                                        <p className="text-sm text-gray-900">
                                          {value === null || value === undefined ? '-' :
                                           typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
                                           String(value)}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Contact Info */}
                                {exception.tenant && (
                                  <div className="flex items-center gap-4 text-sm text-gray-500">
                                    {exception.tenant.email && (
                                      <span>{exception.tenant.email}</span>
                                    )}
                                    {exception.tenant.phone && (
                                      <span>{exception.tenant.phone}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* No Results */}
          {filteredExceptions.length === 0 && (
            <div className="card text-center py-12">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {severityFilter === 'all' ? 'No Issues Found' : `No ${severityFilter} Issues`}
              </h3>
              <p className="text-gray-500">
                {severityFilter === 'all'
                  ? 'Great job! Your facility passed all audit checks.'
                  : 'Try selecting a different severity filter.'}
              </p>
            </div>
          )}
        </>
      )}

      {/* No Audit Results */}
      {!auditResults && !loading && (
        <div className="card text-center py-12">
          <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Recent Audit</h3>
          <p className="text-gray-500 mb-4">
            Run an audit to check your facility for issues.
          </p>
          <button onClick={runAudit} className="btn-primary inline-flex items-center gap-2">
            <Play className="w-4 h-4" />
            Run First Audit
          </button>
        </div>
      )}

      {/* AI Chat Assistant - shown when audit results are available */}
      {auditResults?.audit_id && (
        <AuditChat
          auditId={auditResults.audit_id}
          facilityName={selectedFacility?.name}
        />
      )}
    </div>
  )
}
